import React from 'react';
import {
  MapPin,
  Briefcase,
  Clock,
  CalendarCheck,
  DollarSign
} from 'lucide-react';
import TimedPill from './TimedPill';
import PillList from './PillList';
import InfoRow from './InfoRow';
import CardEditRow from './CardEditRow';
import { buildCompensationLabel, computeAvailability, formatLocationPreference } from './utils';
import SRNABadge from './SRNABadge';
import VerificationBadges from './VerificationBadges';
import ProfileScheduleStrip from './ProfileScheduleStrip';
import ProfileLicensePills, { requiredLicenseDetails } from './ProfileLicensePills';

/**
 * Formats a date string (ISO or YYYY-MM-DD) to a short "Mon D" label.
 * e.g. '2025-07-06' → 'Jul 6'
 */
const shortDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch (_) {
    return dateStr;
  }
};

// onEditRow(key) is the anonymous card-builder seam (keys here: location,
// workType). hideEmptyRows is the shared-/s/-view seam: rows with nothing to
// say are omitted rather than rendered as dashes. Both absent -- every other
// viewer surface -- rendering is unchanged.
const ProfileFront = ({ p = {}, skipVerification = false, alignmentData = null, viewerType = 'provider', onBadgeClick, schedule = null, onEditRow = null, hideEmptyRows = false, onRateWithheldClick = null, presentation = 'card', rateCaption, serviceHeader, availabilityLabel }) => {
  // Better defaults for when properties are undefined or null
  const {
    role = 'CRNA',
    providerType = 'CRNA', // Provider type (CRNA, Anesthesiologist, CAA, SRNA)
    crnaCertificationStatus = 'No',
    // No destructuring defaults here: they fire only for undefined, and both
    // fields routinely arrive as '' or null. formatLocationPreference coalesces.
    locationPreference,
    radiusPreference,
    desiredWorkType = [],
    yearsOfExperience = '—',
    compensation = {},
    availability = {},
    npi,
    isSRNA,
    name,
    _id,
    id,
    providerProfileId
  } = p || {};

  // Get provider ID for verification badges
  const providerId = providerProfileId || _id || id;

  // Get formal name to display based on provider type and verification status
  const getFormalName = () => {
    const isSRNAProvider = isSRNA || crnaCertificationStatus === 'SRNA';

    // SRNAs use self-reported name (no verified NPI)
    if (isSRNAProvider) {
      return name || null;
    }

    // For verified providers, use NPI name
    const npiName = npi?.name;
    if (!npiName) return null;

    // Parse name parts for formal display
    const nameParts = npiName.trim().split(/\s+/);
    const lastName = nameParts[nameParts.length - 1];

    // Anesthesiologists get "Dr." prefix
    if (providerType === 'Anesthesiologist') {
      return `Dr. ${lastName}`;
    }

    // CRNAs and CAAs get suffix
    if (providerType === 'CRNA' || providerType === 'CAA') {
      return `${npiName}, ${providerType}`;
    }

    // Fallback to just the name
    return npiName;
  };

  const formalName = getFormalName();

  // The sanitized anonymous profile carries the credential as `role` (the
  // sanitize allow-list has no providerType), so the title falls back to it
  // rather than defaulting every CAA and Anesthesiologist card to "CRNA".
  // Students first: they are stored as providerType 'CRNA' (the enum has no
  // SRNA) and a student is never presented as a CRNA. The sanitized card
  // carries crnaCertificationStatus, never isSRNA, so the status is the one
  // every recruiter deck can read.
  const isStudent = isSRNA === true || crnaCertificationStatus === 'SRNA';
  const cardTitle = isStudent ? 'SRNA' : p?.providerType || role || 'CRNA';

  // The provider chose to withhold their floor (ProviderProfile.rateVisibility
  // 'private'). sanitizeProfile has already dropped every amount and set this
  // flag, so there is no number here to accidentally render -- this only decides
  // whether the pill reads "Competitive Rate" or the useless "No rate specified".
  // The distinction matters: withheld is not the same fact as never stated, and
  // the gate will tell a recruiter who submits a role that a minimum exists.
  const rateWithheld = !!compensation?.isBlind;

  // The formatting itself lives in utils so RateVisibilityRow can quote this
  // exact string back to the provider ("Employers see $195/hr | $425K/yr") from
  // the raw, unsanitized numbers it holds. Only the withheld branch stays here:
  // isBlind is a property of the sanitized card profile, which that control
  // never sees.
  const compensationLabel = rateWithheld
    ? 'Competitive Rate'
    : buildCompensationLabel(compensation);

  const {
    contractType = [],
    desiredSchedule = [],
    availabilityDate
  } = availability || {};

  // M2 match-level availability chip (only when flag is on and data is present)
  const matchAvailability = p.matchAvailability || null;
  const matchAvailabilityLabel = matchAvailability
    ? (() => {
        const from = shortDate(matchAvailability.window?.from);
        const to = shortDate(matchAvailability.window?.to);
        const near = matchAvailability.nearLocation ? ` near ${matchAvailability.nearLocation}` : '';
        return `Available ${from} to ${to}${near}`;
      })()
    : null;

  const MatchAvailabilityChip = matchAvailabilityLabel ? (
    <InfoRow Icon={CalendarCheck}>
      <TimedPill
        id='match-availability'
        label={matchAvailabilityLabel}
        tooltipText={`Schedule overlap: ${matchAvailability.overlapDays ?? matchAvailability.availableDays ?? ''} days`}
        className='match-availability-chip'
      />
    </InfoRow>
  ) : null;

  // Define reusable field components for ordering
  const hasPay = compensationLabel !== '—' && compensationLabel !== 'No rate specified';
  const hasContract = Array.isArray(contractType) && contractType.length > 0;
  const PayRow = hideEmptyRows && !hasPay && !hasContract ? null : (
    <InfoRow Icon={DollarSign} alignmentStatus={alignmentData?.compensation}>
      <div className="pay-contract-details">
        {/* A withheld floor gets NO tooltip. The listing-side "Competitive
            Rate" badge reveals a market band on tap, but a band drawn around
            one named person's floor is a guess at that person's number, so this
            pill deliberately reveals nothing -- TimedPill renders no overlay at
            all when tooltipText is absent. onRateWithheldClick is a separate
            path (a caller-owned modal explaining the rate-check mechanism,
            not a number), only wired when the floor is actually withheld. */}
        <TimedPill
          id="pay-rate"
          label={compensationLabel}
          tooltipText={
            rateWithheld ? undefined : `Compensation: ${compensationLabel}`
          }
          className={rateWithheld ? 'rate-private-pill' : ''}
          alignmentStatus={alignmentData?.compensation}
          onClick={rateWithheld ? onRateWithheldClick : null}
        />
        {/* Show contract type inline for provider view */}
        {viewerType === 'provider' && (
          <PillList
            items={Array.isArray(contractType) && contractType.length > 0 ? contractType : ['—']}
            getTooltip={() => 'W2 = employed; 1099 = contract'}
            idPrefix="contract"
            alignmentStatus={alignmentData?.contractType}
          />
        )}
      </div>
    </InfoRow>
  );

  // The counterpart of ListingFront's start-date row: one asks when the work
  // begins, the other says when this provider can begin it.
  const AvailabilityRow = (
    <InfoRow Icon={CalendarCheck} alignmentStatus={alignmentData?.startDate}>
      <PillList
        items={[availabilityLabel ?? computeAvailability(availabilityDate)]}
        getTooltip={() => 'Earliest available date'}
        idPrefix="availability"
        alignmentStatus={alignmentData?.startDate}
      />
    </InfoRow>
  );

  // The schedule block's locationText is a stated work location too, so it
  // beats a placeholder when the profile field itself is empty. One pill only:
  // a second element repeating the place breaks getByText. The formatted
  // string doubles as the builder's empty test -- its "Location not specified"
  // placeholder is exactly the case the tap-to-add prompt replaces.
  const locationLabel = formatLocationPreference(locationPreference, radiusPreference, schedule?.locationText);
  const hasLocation = !locationLabel.startsWith('Location not specified');
  const LocationRow = hideEmptyRows && !hasLocation ? null : (
    <CardEditRow
      onEdit={onEditRow ? () => onEditRow('location') : null}
      label="location"
      Icon={MapPin}
      empty={!hasLocation}
      prompt="Add a location"
    >
      <InfoRow Icon={MapPin} alignmentStatus={alignmentData?.location}>
        <PillList
          items={[locationLabel]}
          getTooltip={() => 'Preferred location and commute distance'}
          idPrefix="location"
          alignmentStatus={alignmentData?.location}
        />
      </InfoRow>
    </CardEditRow>
  );

  // In schedule-strip mode PayRow (which normally carries the W2/1099 pills)
  // is suppressed, so contract type joins the work-type row there instead of
  // disappearing. Without a schedule this stays work-type-only -- PayRow
  // already shows the contract pills and doubling them would be noise.
  const contractPills = schedule && Array.isArray(contractType) ? contractType : [];
  const workTypeItems = [
    ...(Array.isArray(desiredWorkType) ? desiredWorkType : []),
    ...contractPills,
  ];
  const DurationRow = hideEmptyRows && workTypeItems.length === 0 ? null : (
    <CardEditRow
      onEdit={onEditRow ? () => onEditRow('workType') : null}
      label="work type"
      Icon={Briefcase}
      empty={workTypeItems.length === 0}
      prompt="Add work type (W2, 1099...)"
    >
      <InfoRow Icon={Briefcase} alignmentStatus={alignmentData?.workType}>
        <PillList
          items={workTypeItems.length > 0 ? workTypeItems : ['—']}
          getTooltip={it => {
            if (it === '—') return 'No work type specified';
            if (it === 'W2' || it === '1099') return 'W2 = employed; 1099 = contract';
            return `Preferred work type: ${it}`;
          }}
          idPrefix="workType"
          alignmentStatus={alignmentData?.workType}
        />
      </InfoRow>
    </CardEditRow>
  );

  // Optional per-schedule strip (service blocks), rendered right after
  // the availability-date row. Additive: absent on every caller that doesn't
  // pass `schedule`, so existing card renders are unchanged.
  // In builder mode the strip is a tap target like every other row: it opens
  // the Availability sheet (days, cadence, shifts, dates, pay) upstream.
  const ScheduleStripRow = schedule ? (
    onEditRow ? (
      <CardEditRow onEdit={() => onEditRow('availability')} label="availability">
        <ProfileScheduleStrip schedule={schedule} rateCaption={rateCaption} />
      </CardEditRow>
    ) : (
      <ProfileScheduleStrip schedule={schedule} rateCaption={rateCaption} />
    )
  ) : null;

  const hasDesiredSchedule = Array.isArray(desiredSchedule) && desiredSchedule.length > 0;
  const ScheduleRow = hideEmptyRows && !hasDesiredSchedule ? null : (
    // The listing's counterpart row (ListingFront) is marked the same way: the
    // row carries the overall verdict, each pill carries its own.
    <InfoRow Icon={CalendarCheck} alignmentStatus={alignmentData?.schedule?.overall}>
      <PillList
        items={hasDesiredSchedule ? desiredSchedule : ['—']}
        getTooltip={it => it === '—' ? 'No schedule preference specified' : `Schedule preference: ${it}`}
        idPrefix="schedule"
        alignmentStatus={hasDesiredSchedule ? alignmentData?.schedule : null}
      />
    </InfoRow>
  );

  // Render fields in role-based order
  // Group users: Availability → MatchAvailabilityChip → Location → Pay → Duration → Schedule
  // Provider users: Pay → Location → Availability → Duration → Schedule
  //
  // When `schedule` is present, ProfileScheduleStrip is the authoritative
  // source for pay, availability-date, and desired-schedule facts (it reads
  // the schedule block, not the raw profile), so the equivalent native rows
  // (PayRow, AvailabilityRow, ScheduleRow) are redundant and get suppressed.
  // Every omission below is gated on `schedule` being present -- when it is
  // absent (every existing caller today: recruiter deck, admin previews,
  // a provider's own profile view) this renders exactly as before.
  const renderOrderedFields = () => {
    if (viewerType === 'group') {
      return (
        <>
          {!schedule && AvailabilityRow}
          {ScheduleStripRow}
          {MatchAvailabilityChip}
          {LocationRow}
          {!schedule && PayRow}
          {DurationRow}
          {!schedule && ScheduleRow && (
            <>
              <hr className='hand-drawn-divider' />
              {ScheduleRow}
            </>
          )}
          <ProfileLicensePills relevance={p.licenseRelevance} />
        </>
      );
    }
    // Provider (default) view
    return (
      <>
        {!schedule && PayRow}
        {LocationRow}
        {!schedule && AvailabilityRow}
        {ScheduleStripRow}
        {/* M2: availability overlap chip. Null unless the flag is on AND the
            card carries matchAvailability (only the group provider deck sets it),
            so this is inert on a provider's own profile view. */}
        {MatchAvailabilityChip}
        {DurationRow && <hr className='hand-drawn-divider' />}
        {DurationRow}
        {!schedule && ScheduleRow && (
          <>
            <hr className='hand-drawn-divider' />
            {ScheduleRow}
          </>
        )}
      </>
    );
  };

  if (presentation === 'service') {
    return (
      <div className="crna-service-facts" role="region" aria-label="Profile Front">
        {serviceHeader || <div className="crna-service-place">
          <span className="crna-service-credential">{cardTitle}</span>
          <span><MapPin size={14} aria-hidden="true" />{locationLabel}</span>
        </div>}
        {schedule ? <ProfileScheduleStrip schedule={schedule} presentation="service" rateCaption={rateCaption} /> : (
          <p className="crna-service-unset">Add dates and a weekly pattern to complete this service.</p>
        )}
        {workTypeItems.length > 0 && (
          <div className="crna-service-worktypes">
            <Briefcase size={14} aria-hidden="true" />
            <PillList items={workTypeItems} getTooltip={(item) => `Preferred work arrangement: ${item}`} idPrefix="service-workType" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`card-face-content${viewerType === 'group' && requiredLicenseDetails(p.licenseRelevance).length ? ' card-face-content--license-status' : ''}`} role="region" aria-label="Profile Front">
      <div className="card-header">
        {/* Builder mode: the credential is a card fact like any other, so it
            is edited here rather than in a chooser bolted above the card. */}
        {onEditRow ? (
          <CardEditRow
            onEdit={() => onEditRow('credential')}
            label="credential"
            className="crna-cardedit--title"
          >
            <h2 className="card-title">{cardTitle}</h2>
          </CardEditRow>
        ) : (
          <h2 className="card-title">{cardTitle}</h2>
        )}
        <div className="badges-container">
          {crnaCertificationStatus === 'SRNA' && <SRNABadge />}
          {providerId && <VerificationBadges providerId={providerId} skipVerification={skipVerification} providerType={providerType} isCertified={crnaCertificationStatus === 'Yes'} onBadgeClick={onBadgeClick} />}
        </div>
      </div>

      {renderOrderedFields()}
    </div>
  );
};

export default ProfileFront;
